import { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, CircleAlert as AlertCircle, Copy, Check, ChevronDown, ChevronUp, CircleCheck as CheckCircle2, Loader as Loader2, Circle, Rocket } from 'lucide-react';
import { useRealtimeStatus } from '../../lib/useRealtimeStatus';
import { canInvokeEdgeFn, releaseEdgeFn } from '../../lib/edgeFnGuard';
import { Sentry } from '../../lib/sentry';
import { useApi } from '../../lib/useApi';
import type { Account, ValuePyramid, ValueStatements } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';
import FormattedText from '../common/FormattedText';
import ValueStatementsSection from './ValueStatementsSection';
import StrategicGoalsSection from './StrategicGoalsSection';

interface Props {
  account: Account;
  onAccountRefresh?: () => void;
  onPyramidChange?: (pyramid: ValuePyramid | null) => void;
  valueStatements?: ValueStatements | null;
  onValueStatementsChange?: (vs: ValueStatements | null) => void;
  vendorIntelPublished?: boolean;
  pyramidCompleted?: boolean;
  isOwner?: boolean;
}

type PyramidStatus = 'pending' | 'processing' | 'completed' | 'error';

type ResearchSection = 'overview' | 'valuePyramid' | 'valueStatements' | 'personaMessaging' | 'fullResearch';

export default function ResearchTab({ account, onAccountRefresh, onPyramidChange, valueStatements, onValueStatementsChange, vendorIntelPublished = false, pyramidCompleted = false, isOwner = true }: Props) {
  const { apiGet, apiPost } = useApi();
  const [pyramid, setPyramid] = useState<ValuePyramid | null>(null);
  const [copied, setCopied] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expandedVerbose, setExpandedVerbose] = useState(false);
  const [selectedSection, setSelectedSection] = useState<ResearchSection>('overview');
  const [deepResearchRunning, setDeepResearchRunning] = useState(false);
  const [deepResearchError, setDeepResearchError] = useState('');
  const deepResearchLockRef = useRef(false);
  const onPyramidChangeRef = useRef(onPyramidChange);
  onPyramidChangeRef.current = onPyramidChange;
  const onAccountRefreshRef = useRef(onAccountRefresh);
  onAccountRefreshRef.current = onAccountRefresh;

  const [liveEnrichStatus, setLiveEnrichStatus] = useState(account.enrichment_status || 'none');
  const [livePyramidStatus, setLivePyramidStatus] = useState<string>(pyramid?.status || 'pending');
  const [liveVsStatus, setLiveVsStatus] = useState<string>(valueStatements?.status || 'pending');
  const [liveSignalsStatus, setLiveSignalsStatus] = useState(account.signals_status || 'none');

  useEffect(() => { setLiveEnrichStatus(account.enrichment_status || 'none'); }, [account.enrichment_status]);
  useEffect(() => { setLiveVsStatus(valueStatements?.status || 'pending'); }, [valueStatements?.status]);
  useEffect(() => { setLiveSignalsStatus(account.signals_status || 'none'); }, [account.signals_status]);

  const status: PyramidStatus = pyramid?.status as PyramidStatus || 'pending';
  const pyramidProcessing = status === 'processing';

  useEffect(() => { setLivePyramidStatus(pyramid?.status || 'pending'); }, [pyramid?.status]);

  const [liveStrategicGoalsStatus, setLiveStrategicGoalsStatus] = useState<string>(pyramid?.strategic_goals_status || 'pending');

  useEffect(() => { setLiveStrategicGoalsStatus(pyramid?.strategic_goals_status || 'pending'); }, [pyramid?.strategic_goals_status]);

  const anyProcessing =
    deepResearchRunning ||
    livePyramidStatus === 'processing' ||
    liveVsStatus === 'processing' ||
    liveSignalsStatus === 'processing' ||
    liveStrategicGoalsStatus === 'processing';

  const orgQuery = `org_id=${encodeURIComponent(account.org_id)}`;

  const loadPipelineStatuses = useCallback(async () => {
    // value_pyramids + value_statements come from GET /v1/account/{id}/value
    // (requireAccountAccess = legacy can_access_account SELECT RLS); enrichment/
    // signals status come from GET /v1/account/{id}. Replaces the prior Supabase reads.
    const [value, acct] = await Promise.all([
      apiGet<{ pyramid: ValuePyramid | null; value_statements: ValueStatements | null }>(
        `/v1/account/${account.id}/value?${orgQuery}`,
      ),
      apiGet<{ account: Account }>(`/v1/account/${account.id}?${orgQuery}`),
    ]);
    if (value.pyramid) {
      setPyramid(value.pyramid);
      onPyramidChangeRef.current?.(value.pyramid);
      setLivePyramidStatus(value.pyramid.status);
      setLiveStrategicGoalsStatus(value.pyramid.strategic_goals_status || 'pending');
    }
    if (value.value_statements) setLiveVsStatus(value.value_statements.status);
    if (acct.account) {
      setLiveEnrichStatus(acct.account.enrichment_status || 'none');
      setLiveSignalsStatus(acct.account.signals_status || 'none');
    }
  }, [apiGet, account.id, orgQuery]);

  const loadPyramid = useCallback(async () => {
    const { pyramid: data } = await apiGet<{ pyramid: ValuePyramid | null }>(
      `/v1/account/${account.id}/value?${orgQuery}`,
    );
    if (data) {
      setPyramid(data);
      onPyramidChangeRef.current?.(data);
      setLivePyramidStatus(data.status);
    }
    setLoaded(true);
    return data;
  }, [apiGet, account.id, orgQuery]);

  useEffect(() => {
    loadPyramid();
  }, [loadPyramid]);

  useEffect(() => {
    if (!anyProcessing) return;
    const interval = setInterval(loadPipelineStatuses, 4000);
    return () => clearInterval(interval);
  }, [anyProcessing, loadPipelineStatuses]);

  useRealtimeStatus({
    table: 'value_pyramids',
    filterColumn: 'account_id',
    filterValue: account.id,
    enabled: pyramidProcessing,
    statusField: 'status',
    terminalStatuses: ['completed', 'error'],
    fallbackIntervalMs: 3000,
    // Poll the value pyramid through the signed GET /v1/account/{id}/value read.
    fetcher: useCallback(async () => {
      const { pyramid: row } = await apiGet<{ pyramid: ValuePyramid | null }>(
        `/v1/account/${account.id}/value?${orgQuery}`,
      );
      return row ? (row as unknown as Record<string, unknown>) : null;
    }, [apiGet, account.id, orgQuery]),
    onUpdate: useCallback((row: Record<string, unknown>) => {
      const data = row as unknown as ValuePyramid;
      setPyramid(data);
      setLivePyramidStatus(data.status);
      onPyramidChangeRef.current?.(data);
      if (data.status === 'completed') {
        onAccountRefreshRef.current?.();
      }
    }, []),
  });

  useRealtimeStatus({
    table: 'value_pyramids',
    filterColumn: 'account_id',
    filterValue: account.id,
    enabled: pyramid?.strategic_goals_status === 'processing',
    statusField: 'strategic_goals_status',
    terminalStatuses: ['completed', 'error'],
    fallbackIntervalMs: 4000,
    // strategic_goals_status lives on the value_pyramids row → same /value read.
    fetcher: useCallback(async () => {
      const { pyramid: row } = await apiGet<{ pyramid: ValuePyramid | null }>(
        `/v1/account/${account.id}/value?${orgQuery}`,
      );
      return row ? (row as unknown as Record<string, unknown>) : null;
    }, [apiGet, account.id, orgQuery]),
    onUpdate: useCallback((row: Record<string, unknown>) => {
      const data = row as unknown as ValuePyramid;
      setPyramid(prev => (prev ? { ...prev, ...data } : data));
      onPyramidChangeRef.current?.(data);
    }, []),
  });

  useRealtimeStatus({
    table: 'value_statements',
    filterColumn: 'account_id',
    filterValue: account.id,
    enabled: liveVsStatus === 'processing',
    statusField: 'status',
    terminalStatuses: ['completed', 'error'],
    fallbackIntervalMs: 3000,
    // Poll the value_statements row through the signed GET /v1/account/{id}/value read.
    fetcher: useCallback(async () => {
      const { value_statements: row } = await apiGet<{ value_statements: ValueStatements | null }>(
        `/v1/account/${account.id}/value?${orgQuery}`,
      );
      return row ? (row as unknown as Record<string, unknown>) : null;
    }, [apiGet, account.id, orgQuery]),
    onUpdate: useCallback((row: Record<string, unknown>) => {
      const data = row as unknown as ValueStatements;
      onValueStatementsChange?.(data);
      if (data.status === 'completed') {
        onAccountRefreshRef.current?.();
      }
    }, [onValueStatementsChange]),
  });

  const copyToClipboard = async () => {
    if (!pyramid || pyramid.status !== 'completed') return;
    const lines: string[] = [];

    if (pyramid.corporate_objectives.length > 0) {
      lines.push('Corporate Objectives');
      pyramid.corporate_objectives.forEach((item) => {
        lines.push(`  - ${item.claim}`);
      });
      lines.push('');
    }
    if (pyramid.business_strategy.length > 0) {
      lines.push('Business Initiatives');
      pyramid.business_strategy.forEach((item) => {
        lines.push(`  - ${item.claim}`);
      });
      lines.push('');
    }
    if (pyramid.technical_initiatives.length > 0) {
      lines.push('Technology Initiatives');
      pyramid.technical_initiatives.forEach((item) => {
        lines.push(`  - ${item.claim}`);
      });
    }

    await navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const startDeepResearch = () => {
    if (deepResearchLockRef.current) return;
    if (!canInvokeEdgeFn(`deep-research-${account.id}`)) return;
    deepResearchLockRef.current = true;
    setDeepResearchRunning(true);
    setDeepResearchError('');
    setLivePyramidStatus('pending');
    setLiveVsStatus('pending');
    setLiveSignalsStatus('processing');

    apiPost('/v1/research-pipeline', { account_id: account.id })
      .catch((err: unknown) => {
        Sentry.captureException(err, { tags: { context: 'run-deep-research' } });
        setDeepResearchRunning(false);
        deepResearchLockRef.current = false;
        releaseEdgeFn(`deep-research-${account.id}`);
        setDeepResearchError(err instanceof Error ? err.message : 'Failed to start deep research');
      });
  };

  useEffect(() => {
    if (!deepResearchRunning) return;
    if (
      livePyramidStatus !== 'processing' && livePyramidStatus !== 'pending' &&
      liveVsStatus !== 'processing' && liveVsStatus !== 'pending' &&
      liveSignalsStatus !== 'processing'
    ) {
      setDeepResearchRunning(false);
      deepResearchLockRef.current = false;
      releaseEdgeFn(`deep-research-${account.id}`);
    }
  }, [deepResearchRunning, livePyramidStatus, liveVsStatus, liveSignalsStatus, account.id]);

  const completed = status === 'completed';
  const hasError = status === 'error';
  const showLoading = pyramidProcessing;

  const pipelinePhases: PipelinePhase[] = [
    { label: 'Value Pyramid', status: livePyramidStatus === 'completed' ? 'completed' : livePyramidStatus === 'processing' ? 'processing' : 'pending' },
    { label: 'Value Statements', status: liveVsStatus === 'completed' ? 'completed' : liveVsStatus === 'processing' ? 'processing' : 'pending' },
    { label: 'Trigger Signals', status: liveSignalsStatus === 'completed' ? 'completed' : liveSignalsStatus === 'processing' ? 'processing' : 'pending' },
  ];

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isOwner && (
        <div className="flex items-center gap-3 p-4 bg-app-focus/10 border border-app-focus rounded-lg">
          <svg className="w-5 h-5 text-app-focus flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-sm text-app-focus">View only. Research is managed by the account owner.</p>
        </div>
      )}
      <div className="flex flex-col gap-4 p-4 bg-app-surface border border-forest-200/60 rounded-lg">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-sm font-medium text-forest-700">Research Hub</p>
            <h1 className="text-2xl font-semibold text-app-ink">Deep account intelligence, organised for fast review</h1>
            <p className="text-sm text-app-muted mt-2 max-w-2xl">
              Explore each core research area in its own dedicated space. Value pyramid, value statements, persona messaging, and raw intelligence are all accessible from a polished tile menu.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {completed && (
              <button onClick={copyToClipboard} className="btn-ghost !py-2.5 !px-4 !text-sm">
                {copied
                  ? <><Check className="w-4 h-4 mr-2 text-app-positive" /><span className="text-app-positive">Copied</span></>
                  : <><Copy className="w-4 h-4 mr-2" />Copy Summary</>
                }
              </button>
            )}
            {isOwner && (
            <button
              onClick={startDeepResearch}
              disabled={anyProcessing || account.enrichment_status !== 'completed'}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition duration-200 ${
                anyProcessing
                  ? 'bg-app-canvas text-app-muted cursor-not-allowed'
                  : account.enrichment_status !== 'completed'
                    ? 'bg-app-canvas text-app-muted cursor-not-allowed'
                    : 'bg-forest-600 text-white hover:bg-forest-700 shadow-none hover:shadow-none'
              }`}
              title={account.enrichment_status !== 'completed' ? 'Complete the Overview step first' : ''}
            >
              {anyProcessing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Running...</>
              ) : (
                <><Rocket className="w-4 h-4" /> Run Deep Research</>
              )}
            </button>
            )}
          </div>
        </div>
        {deepResearchError && (
          <div className="flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg text-sm text-app-critical">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {deepResearchError}
          </div>
        )}
      </div>

      {anyProcessing && <PipelineProgressBar phases={pipelinePhases} />}

      {selectedSection === 'overview' ? (
        <div className="space-y-6">
          <StrategicGoalsSection
            goals={pyramid?.strategic_goals ?? null}
            status={pyramid?.strategic_goals_status ?? null}
            companyName={account.company_name}
          />

          <SectionTileGrid onSelect={setSelectedSection} completed={completed} pyramid={pyramid} valueStatements={valueStatements} pipelinePhases={pipelinePhases} />

          {!anyProcessing && hasError && (
            <div className="bg-app-critical/10 border border-app-critical rounded-lg p-8 text-center">
              <AlertCircle className="w-12 h-12 text-app-critical mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-app-critical mb-2">Research Failed</h3>
              <p className="text-sm text-app-critical mb-4">
                Something went wrong during analysis. Please try running deep research again.
              </p>
            </div>
          )}

          {!pyramid && !anyProcessing && (
            <div className="bg-app-surface border border-forest-100 rounded-lg p-10 text-center shadow-none">
              <Brain className="w-12 h-12 text-forest-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-app-ink mb-2">No Research Yet</h3>
              <p className="text-sm text-app-muted mb-6 max-w-xl mx-auto">
                {account.enrichment_status === 'completed'
                  ? `Click "Run Deep Research" above to generate the full intelligence package for ${account.company_name}.`
                  : `Complete the Overview step first by populating account intelligence, then return here to run deep research for ${account.company_name}.`
                }
              </p>
              {isOwner && account.enrichment_status === 'completed' && (
                <button
                  onClick={startDeepResearch}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold bg-forest-600 text-white hover:bg-forest-700 shadow-none hover:shadow-none transition duration-200"
                >
                  <Rocket className="w-4 h-4" /> Run Deep Research
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <SectionDetailView
          section={selectedSection}
          onBack={() => setSelectedSection('overview')}
          pyramid={pyramid}
          showLoading={showLoading}
          completed={completed}
          valueStatements={valueStatements}
          vendorIntelPublished={vendorIntelPublished}
          pyramidCompleted={pyramidCompleted || pyramid?.status === 'completed'}
          account={account}
          onValueStatementsChange={onValueStatementsChange}
          onAccountRefresh={onAccountRefresh}
          expandedVerbose={expandedVerbose}
          setExpandedVerbose={setExpandedVerbose}
        />
      )}
    </div>
  );
}

interface PipelinePhase {
  label: string;
  status: 'pending' | 'processing' | 'completed';
}

function PipelineProgressBar({ phases }: { phases: PipelinePhase[] }) {
  const completedCount = phases.filter(p => p.status === 'completed').length;
  const processingPhase = phases.find(p => p.status === 'processing');
  const progressPct = Math.round(((completedCount + (processingPhase ? 0.5 : 0)) / phases.length) * 100);

  return (
    <div className="bg-forest-50 border border-forest-200 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-5">
        <Brain className="w-5 h-5 text-forest-600 animate-pulse" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-forest-900">Pipeline Running</h3>
            <span className="text-xs font-medium text-forest-600">{progressPct}%</span>
          </div>
          <p className="text-xs text-forest-700 mt-0.5">
            {processingPhase ? `${processingPhase.label}...` : 'Processing...'} You can navigate away safely.
          </p>
        </div>
      </div>

      <div className="h-2 rounded-full overflow-hidden bg-app-line mb-4" role="progressbar" aria-label="Account research" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPct}>
        <div
          className="h-full rounded-full bg-app-accent transition duration-700 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="flex items-center gap-1">
        {phases.map((phase, i) => (
          <div key={phase.label} className="flex items-center gap-1 flex-1 min-w-0">
            {phase.status === 'completed' ? (
              <CheckCircle2 className="w-4 h-4 text-forest-600 flex-shrink-0" />
            ) : phase.status === 'processing' ? (
              <Loader2 className="w-4 h-4 text-forest-500 animate-spin flex-shrink-0" />
            ) : (
              <Circle className="w-4 h-4 text-forest-300 flex-shrink-0" />
            )}
            <span className={`text-xs truncate ${
              phase.status === 'completed' ? 'text-forest-700 font-medium' :
              phase.status === 'processing' ? 'text-forest-600 font-medium' :
              'text-forest-400'
            }`}>
              {phase.label}
            </span>
            {i < phases.length - 1 && (
              <div className={`h-px flex-shrink-0 w-3 mx-0.5 ${phase.status === 'completed' ? 'bg-forest-400' : 'bg-forest-200'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionTileGrid({ onSelect, completed, pyramid, valueStatements, pipelinePhases }: {
  onSelect: (section: ResearchSection) => void;
  completed: boolean;
  pyramid: ValuePyramid | null;
  valueStatements?: ValueStatements | null;
  pipelinePhases: PipelinePhase[];
}) {
  const phaseStatus = (label: string) => pipelinePhases.find(p => p.label === label)?.status || 'pending';

  const tileStatus = (key: ResearchSection): { text: string; variant: 'ready' | 'processing' | 'default' } => {
    switch (key) {
      case 'valuePyramid': {
        const s = phaseStatus('Value Pyramid');
        if (s === 'completed') return { text: 'Ready', variant: 'ready' };
        if (s === 'processing') return { text: 'Processing', variant: 'processing' };
        return { text: 'Not started', variant: 'default' };
      }
      case 'valueStatements': {
        const s = phaseStatus('Value Statements');
        if (s === 'completed' || valueStatements?.status === 'completed') return { text: 'Ready', variant: 'ready' };
        if (s === 'processing') return { text: 'Processing', variant: 'processing' };
        return { text: 'Not started', variant: 'default' };
      }
      case 'personaMessaging':
        if (valueStatements?.status === 'completed') return { text: 'Ready', variant: 'ready' };
        return { text: 'Needs value statements', variant: 'default' };
      case 'fullResearch':
        if (completed && pyramid?.raw_response) return { text: 'Ready', variant: 'ready' };
        return { text: 'Needs value pyramid', variant: 'default' };
      default:
        return { text: '', variant: 'default' };
    }
  };

  const tiles: Array<{ key: ResearchSection; title: string; description: string }> = [
    { key: 'valuePyramid', title: 'Value Pyramid', description: 'Structured business and technology outcomes for the account.' },
    { key: 'valueStatements', title: 'Value Statements', description: 'Generate and refine messaging that resonates with buyers.' },
    { key: 'personaMessaging', title: 'Persona Messaging', description: 'Insights for buyer personas, key pain points and positioning.' },
    { key: 'fullResearch', title: 'Full research', description: 'Review the complete research output and supporting analysis.' },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {tiles.map((tile) => {
        const ts = tileStatus(tile.key);
        return (
          <button
            key={tile.key}
            type="button"
            onClick={() => onSelect(tile.key)}
            className="group text-left rounded-lg border border-app-line border-l-[3px] border-l-forest-400 bg-app-surface p-6 shadow-none transition duration-300 hover:-translate-y-1 hover:shadow-none hover:border-l-forest-500 hover:bg-forest-50/20"
          >
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <p className="text-sm font-semibold text-app-ink group-hover:text-forest-800 transition-colors duration-300">{tile.title}</p>
                <p className="mt-2 text-sm text-app-muted leading-relaxed">{tile.description}</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
                ts.variant === 'ready' ? 'bg-forest-50 text-forest-700 group-hover:bg-forest-100' :
                ts.variant === 'processing' ? 'bg-app-warning/10 text-app-warning' :
                'bg-app-canvas text-app-muted group-hover:bg-app-canvas'
              }`}>
                {ts.variant === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
                {ts.text}
              </span>
            </div>
            <div className="mt-4 text-sm text-app-muted group-hover:text-forest-600 transition-colors duration-300">
              View details and insights for this research area.
            </div>
          </button>
        );
      })}
    </div>
  );
}

function SectionDetailView({
  section,
  onBack,
  pyramid,
  showLoading,
  completed,
  valueStatements,
  vendorIntelPublished,
  pyramidCompleted,
  account,
  onValueStatementsChange,
  onAccountRefresh,
  expandedVerbose,
  setExpandedVerbose,
}: {
  section: ResearchSection;
  onBack: () => void;
  pyramid: ValuePyramid | null;
  showLoading: boolean;
  completed: boolean;
  valueStatements?: ValueStatements | null;
  pyramidCompleted: boolean;
  account: Account;
  onValueStatementsChange?: (vs: ValueStatements | null) => void;
  onAccountRefresh?: () => void;
  vendorIntelPublished: boolean;
  expandedVerbose: boolean;
  setExpandedVerbose: (value: boolean) => void;
}) {
  const renderSection = () => {
    switch (section) {
      case 'valuePyramid':
        return (
          <div className="space-y-6">
            <section className="rounded-lg border border-forest-200/50 border-l-[3px] border-l-forest-500 bg-app-surface p-8 shadow-none">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-app-ink">Value Pyramid</p>
                  <p className="mt-2 text-sm text-app-muted max-w-2xl">
                    A detailed view of the corporate, business, and technology outcomes generated for {account.company_name}.
                  </p>
                </div>
              </div>
            </section>

            {showLoading && (
              <div className="flex items-center gap-3 p-4 bg-forest-50 border border-forest-100 rounded-lg">
                <Loader2 className="w-5 h-5 text-forest-600 animate-spin" />
                <p className="text-sm font-medium text-forest-800">Building value pyramid...</p>
              </div>
            )}

            {!showLoading && completed && pyramid && (
              <div className="grid gap-5 xl:grid-cols-3">
                <BulletSection title="Corporate Objectives" items={pyramid.corporate_objectives} color="forest" />
                <BulletSection title="Business Initiatives" items={pyramid.business_strategy} color="blue" />
                <BulletSection title="Technology Initiatives" items={pyramid.technical_initiatives} color="teal" />
              </div>
            )}

            <StrategicGoalsSection
              goals={pyramid?.strategic_goals ?? null}
              status={pyramid?.strategic_goals_status ?? null}
              companyName={account.company_name}
            />

            {!showLoading && pyramid?.raw_response && (
              <div className="bg-app-surface rounded-lg border border-forest-100 shadow-none">
                <button
                  onClick={() => setExpandedVerbose(!expandedVerbose)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-forest-50/30 transition-colors duration-200 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-forest-500" />
                    <span className="text-sm font-semibold text-app-ink">Full Research Output</span>
                  </div>
                  {expandedVerbose
                    ? <ChevronUp className="w-4 h-4 text-forest-400" />
                    : <ChevronDown className="w-4 h-4 text-forest-400" />
                  }
                </button>
                {expandedVerbose && (
                  <div className="px-5 pb-5 border-t border-forest-100">
                    <div className="mt-4 text-app-muted text-sm">
                      <FormattedText text={pyramid.raw_response} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'valueStatements':
        return (
          <div className="space-y-6">
            <section className="rounded-lg border border-forest-200/50 border-l-[3px] border-l-forest-500 bg-app-surface p-8 shadow-none">
              <div>
                <p className="text-sm font-semibold text-app-ink">Value Statements</p>
                <p className="mt-2 text-sm text-app-muted max-w-2xl">
                  Create, browse, and refine the messaging framework used for outreach and stakeholder narratives.
                </p>
              </div>
            </section>
            <ValueStatementsSection
              account={account}
              valueStatements={valueStatements ?? null}
              onValueStatementsChange={onValueStatementsChange}
              onAccountRefresh={onAccountRefresh}
              vendorIntelPublished={vendorIntelPublished}
              pyramidCompleted={pyramidCompleted}
              showPersonaSections={false}
            />
          </div>
        );

      case 'personaMessaging':
        return (
          <div className="space-y-6">
            <section className="rounded-lg border border-forest-200/50 border-l-[3px] border-l-forest-500 bg-app-surface p-8 shadow-none">
              <div>
                <p className="text-sm font-semibold text-app-ink">Persona Messaging</p>
                <p className="mt-2 text-sm text-app-muted max-w-2xl">
                  Review tailored messaging and buyer persona signals that make outreach feel relevant.
                </p>
              </div>
            </section>
            <ValueStatementsSection
              account={account}
              valueStatements={valueStatements ?? null}
              onValueStatementsChange={onValueStatementsChange}
              onAccountRefresh={onAccountRefresh}
              vendorIntelPublished={vendorIntelPublished}
              pyramidCompleted={pyramidCompleted || pyramid?.status === 'completed'}
              personaMessagingView
            />
          </div>
        );

      case 'fullResearch':
        return (
          <div className="space-y-6">
            <section className="rounded-lg border border-forest-200/50 border-l-[3px] border-l-forest-500 bg-app-surface p-8 shadow-none">
              <div>
                <p className="text-sm font-semibold text-app-ink">Full Research Output</p>
                <p className="mt-2 text-sm text-app-muted max-w-2xl">
                  Access the source analysis used to generate every research area for the account.
                </p>
              </div>
            </section>
            {!showLoading && pyramid?.raw_response ? (
              <div className="bg-app-surface rounded-lg border border-forest-100 shadow-none p-6">
                <FormattedText text={pyramid.raw_response} />
              </div>
            ) : (
              <div className="bg-forest-50/30 border border-forest-100 rounded-lg p-8 text-center text-app-muted">
                Full research output appears here once the value pyramid analysis completes.
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="group inline-flex items-center gap-2 text-sm font-semibold text-forest-700 hover:text-forest-900 transition-colors duration-200"
      >
        <span aria-hidden="true" className="inline-block transition-transform duration-200 group-hover:-translate-x-1">←</span> Back to Research overview
      </button>
      {renderSection()}
    </div>
  );
}


function BulletSection({ title, items, color }: {
  title: string;
  items: { claim: string }[];
  color: string;
}) {
  const colorMap: Record<string, { dot: string; heading: string; bg: string; topBorder: string }> = {
    forest: { dot: 'bg-forest-500', heading: 'text-forest-800', bg: 'bg-forest-50/30', topBorder: 'border-t-forest-400' },
    blue: { dot: 'bg-app-focus', heading: 'text-app-focus', bg: 'bg-app-focus/10', topBorder: 'border-t-blue-400' },
    teal: { dot: 'bg-app-focus/100', heading: 'text-app-focus', bg: 'bg-app-focus/10', topBorder: 'border-t-teal-400' },
  };
  const c = colorMap[color] || colorMap.forest;

  if (items.length === 0) return null;

  return (
    <div className={`bg-app-surface rounded-lg border border-app-line border-t-2 ${c.topBorder} shadow-none p-6 hover:shadow-none transition duration-300`}>
      <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 ${c.heading}`}>{title}</h3>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${c.bg} transition-colors duration-200`}>
            <div className={`w-2 h-2 rounded-full ${c.dot} mt-1.5 flex-shrink-0`} />
            <p className="text-sm text-app-ink leading-relaxed">{item.claim}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
